import { useCallback, useState } from 'react';
import type { WorkLocation } from '@/store/useTimeclockStore';
import { useAuthStore } from '@/store/useAuthStore';
import { useVehicleStamp, type VehicleStampEvent } from '@/hooks/useVehicleStamp';
import { useWorkLocationQuestion } from '@/hooks/useWorkLocationQuestion';
import { isHomeOffice } from '@/utils/workLocation';

/**
 * Clock-in and clock-out with every question asked before stamping, one after the other:
 *
 * 1. Work location — clock-in only, and only for employees the administration allows to work
 *    from home: "Homeoffice oder Büro?" (useWorkLocationQuestion). The permission is re-read
 *    first, so a change by the administration counts without signing in again.
 * 2. Trip (FAFD-68) — home office starts no trip; otherwise the trip questions of
 *    useVehicleStamp: start a trip with the clock-in, end the running one with the clock-out.
 *
 * Each question awaits its own dialog, so neither can swallow the other. Render the dialogs with
 * `<ClockStampDialogs clockStamp={...} />`. Compose it after useClockOutGuard — the checklist
 * warning comes first. `stamp` resolves once the stamp is recorded or a dialog was cancelled,
 * and rethrows the store's error.
 */
export function useClockStamp() {
  const vehicleStamp = useVehicleStamp();
  const locationQuestion = useWorkLocationQuestion();
  const stampWithVehicle = vehicleStamp.stamp;
  const askWorkLocation = locationQuestion.ask;
  // Covers the lookups before the first dialog, so the stamp buttons cannot fire twice.
  const [checking, setChecking] = useState(false);

  const stamp = useCallback(
    async (eventType: VehicleStampEvent, date?: string, timestamp?: string) => {
      let workLocation: WorkLocation | undefined;

      if (eventType === 'clockIn') {
        setChecking(true);
        try {
          await useAuthStore.getState().fetchCurrentUser();
        } finally {
          setChecking(false);
        }
        if (useAuthStore.getState().employee?.allowsHomeOffice) {
          const chosen = await askWorkLocation();
          if (chosen === null) return;
          workLocation = chosen;
        }
      }

      await stampWithVehicle(eventType, date, timestamp, {
        workLocation,
        noVehicle: isHomeOffice(workLocation),
      });
    },
    [askWorkLocation, stampWithVehicle],
  );

  return {
    stamp,
    preparing: checking || vehicleStamp.preparing,
    tripStartDialog: vehicleStamp.startDialog,
    endTripDialog: vehicleStamp.endDialog,
    locationDialog: locationQuestion.dialog,
  };
}

export type ClockStamp = ReturnType<typeof useClockStamp>;
